import PageHeader from "@/components/PageHeader";
import { Users } from "lucide-react";

const breakdown = [
  { label: "Women", value: 45_940, color: "hsl(var(--primary))" },
  { label: "Men", value: 31_901, color: "hsl(var(--primary-glow))" },
  { label: "Girls", value: 70_441, color: "hsl(var(--accent))" },
  { label: "Boys", value: 75_351, color: "hsl(28 80% 45%)" },
  { label: "IDPs (including drought & conflict-induced displacement)", value: 146_535, color: "hsl(122, 80%, 45%)" },
];

const services = [
  { service: "Adaptive Agriculture and Rangeland Rehabilitation (A2R2)", year: "2023-2028", beneficiaries: "112,400", regions: "Hirshabelle, Galmudug, Benadir" },
  { service: "Response Initiative for Somalia Emergencies (RISE)", year: "2022-2023", beneficiaries: "286,300", regions: "Hirshabelle" },
  { service: "Resilient Livelihood Action to COVID-19 (RLAC-19)", year: "2023", beneficiaries: "48,200 HHs", regions: "Galmudug, Hirshabelle" },
  { service: "Access to Reliable Energy (ARE)", year: "2021", beneficiaries: "175,000", regions: "Beledweyne" },
  { service: "Smart Agriculture for Youth", year: "2020-2021", beneficiaries: "9,640 youths", regions: "Mogadishu, Deynille" },
];

const total = breakdown.reduce((s, b) => s + b.value, 0);

const Impact = () => (
  <>
    <PageHeader
      eyebrow="Our impact"
      title="Reaching 369,633 people across Somalia."
      subtitle="A snapshot of beneficiaries served and services delivered through our integrated programs."
    />

    {/* Beneficiaries graphic */}
    <section className="section-padding">
      <div className="container-wide">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-[var(--shadow-elegant)] md:p-14">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Total reached</p>
              <p className="mt-3 font-serif text-6xl font-semibold leading-none text-primary md:text-7xl">
                {total.toLocaleString()}+
              </p>
              <p className="mt-3 text-lg text-muted-foreground">beneficiaries to date</p>
              <div className="mt-8 flex items-center gap-3 rounded-md bg-secondary p-4 text-sm text-muted-foreground">
                <Users className="h-5 w-5 text-primary" />
                Disaggregated by sex and age across all programs and regions.
              </div>
            </div>

            <div className="space-y-5">
              {breakdown.map((b) => {
                const pct = (b.value / total) * 100;
                return (
                  <div key={b.label}>
                    <div className="mb-2 flex items-baseline justify-between">
                      <span className="font-medium text-foreground">{b.label}</span>
                      <span className="font-serif text-xl font-semibold text-primary">
                        {b.value.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${pct}%`, backgroundColor: b.color }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{pct.toFixed(1)}%</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Services table */}
    <section className="section-padding bg-secondary">
      <div className="container-wide">
        <div className="mb-10 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Services delivered</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">
            Summary of services provided in Somalia
          </h2>
          <p className="mt-4 text-muted-foreground">
            A non-exhaustive overview of voluntary services delivered by our teams and partners.
          </p>
        </div>
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-primary text-primary-foreground">
                <tr>
                  <th className="px-6 py-4 font-serif font-semibold">Service</th>
                  <th className="px-6 py-4 font-serif font-semibold">Period</th>
                  <th className="px-6 py-4 font-serif font-semibold">Beneficiaries</th>
                  <th className="px-6 py-4 font-serif font-semibold">Regions</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s, i) => (
                  <tr key={s.service} className={i % 2 ? "bg-secondary/40" : ""}>
                    <td className="px-6 py-4 font-medium text-foreground">{s.service}</td>
                    <td className="px-6 py-4 text-muted-foreground">{s.year}</td>
                    <td className="px-6 py-4 font-semibold text-primary">{s.beneficiaries}</td>
                    <td className="px-6 py-4 text-muted-foreground">{s.regions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Impact;
